/**
 * 最小 IndexedDB 封装 —— 和司机端那份是同一套写法。
 *
 * ★ 不引 Dexie(~25KB):需求只是一个 store 的增删查。
 *   也不用 localStorage —— 它是**同步**的,主线程上读写几百 KB 的
 *   订单列表会卡住渲染,而这一层存在的全部理由就是「不让人等」。
 *
 * ★ 和司机端各存一份,不共用一个库名:两端可能在同一台电脑的
 *   同一个浏览器里开着(调度员自己也装司机端看效果),
 *   共用的话退出一端会清掉另一端的缓存。
 */

const DB_NAME = 'delivery-admin';
const DB_VERSION = 1;
const CACHE = 'cache';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CACHE)) {
        db.createObjectStore(CACHE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function run<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(CACHE, mode);
        const req = fn(t.objectStore(CACHE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const idbCache = {
  put: <T>(value: T): Promise<IDBValidKey> => run('readwrite', (s) => s.put(value)),
  get: <T>(id: string): Promise<T | undefined> => run('readonly', (s) => s.get(id)),
  clear: (): Promise<undefined> => run('readwrite', (s) => s.clear()),
  /**
   * 按 id 前缀取出若干条。
   *
   * ★ 为「订单状态变了,把缓存里那条也改掉」而加。
   *   那件事原来只改**内存**里的那份,而内存是 24 条的 LRU ——
   *   地图那条被挤出去之后,这一下什么都没改到,
   *   返回地图时从这里读到的还是带着那个点的旧数据。
   *   用户当初报的「结束订单后回到地图上直接不显示」就这么失效了,
   *   而且只在他多翻几页之后才失效 —— 最难复现的那一类。
   *
   * ★ 用游标按前缀扫,不是 getAll 全表:这个 store 里还躺着
   *   别的页面几百 KB 的缓存,整份取出来只为了改两条不划算。
   */
  byPrefix: <T>(prefix: string): Promise<T[]> =>
    open().then(
      (db) =>
        new Promise<T[]>((resolve, reject) => {
          const out: T[] = [];
          const t = db.transaction(CACHE, 'readonly');
          const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
          const req = t.objectStore(CACHE).openCursor(range);
          req.onsuccess = () => {
            const cur = req.result;
            if (!cur) return resolve(out);
            out.push(cur.value as T);
            cur.continue();
          };
          req.onerror = () => reject(req.error);
        }),
    ),
};
